import * as Yup from 'yup';
import { get } from 'lodash';
import { EMAIL_REG_EX } from '../utils/regex';
import {
  INSTANCE_PHONE_REG_EX,
  INSTANCE_PHONE_MIN_LENGTH,
} from '../utils/instanceConstant';

import {
  isGrantLinkedActivity,
  isGrantProgramActivity,
  isParentProgramActivity,
} from '../utils/activity';
import {
  PARTNER_RELATIONSHIP,
  PARTNER_RELATIONSHIP_VALUES,
  CREATE_LINKED_MANAGED_BY_OPTIONS,
} from '../utils/constant';
const grantIdPattern = /^[a-zA-Z0-9]*$/;

export const getLinkedActivitiesFormData = (
  activity,
  projected,
  activityPlanTypeId,
  parentActivityPlanTypeId,

  currentUserEmail = null
) => {
  // GRANT PROGRAM, FESTIVAL PROGRAM, MULTI PROGRAM
  const isProgramActivity = isParentProgramActivity(activityPlanTypeId);

  if (activity) {
    let formData = {
      primaryObjective: projected
        ? isProgramActivity
          ? activity.primaryParentOutcomes || []
          : activity.primaryOutcomeId || ''
        : isProgramActivity
          ? activity.primaryParentOutcomes || []
          : get(activity, 'objective[0].outcome.id', ''),
      secondaryObjective: projected
        ? isProgramActivity
          ? activity.secondaryParentOutcomes || []
          : activity.secondaryOutcomeId || ''
        : isProgramActivity
          ? activity.secondaryParentOutcomes || []
          : get(activity, 'objective[1].outcome.id', ''),
      primaryObjectiveText: isProgramActivity
        ? activity.primaryParentObjectives || []
        : [],
      secondaryObjectiveText: isProgramActivity
        ? activity.secondaryParentObjectives || []
        : [],
      name: activity.name || '',
      activityNotes: activity.activityNotes || '',
      firstName: activity.firstName || '',
      grantId: activity.grantId || '',
      surname: get(activity, 'owner.surname', ''),
      email: get(activity, 'owner.email', ''),
      phone: get(activity, 'owner.phone', ''),
      entityName: get(activity, 'owner.organisation.name', ''),
      country: get(activity, 'country', ''),
      address1: get(activity, 'owner.address1', ''),
      address2: get(activity, 'owner.address2', ''),
      suburb: get(activity, 'owner.suburb', ''),
      state: get(activity, 'owner.state', ''),
      postcode: get(activity, 'owner.postcode', ''),
      projectedGrantAmount: get(activity, 'projectedGrantAmount', null),
      description: activity.description || '',
      organisationId: get(activity, 'owner.organisationId', null),
      isExistingUser: get(activity, 'owner.id', null), // if owner id  is not null -> existing user
      relationship: activity.relationship || PARTNER_RELATIONSHIP[0].value,
      managedBy: CREATE_LINKED_MANAGED_BY_OPTIONS()[0].value,
      emailConfirmError: activity ? 'confirmedUser' : '',
    };

    // GRANT PROGRAM
    if (isGrantProgramActivity(activityPlanTypeId)) {
      formData.projectedTotalGrantPool =
        parseFloat(activity.projectedTotalGrantPool).toFixed(2) || 0.0;
    } else {
      // Projected Program Contribution
      formData.projectedGrantAmount =
        activity.projectedGrantAmount !== null &&
        activity.projectedGrantAmount !== undefined
          ? parseFloat(activity.projectedGrantAmount).toFixed(2)
          : null;
    }

    //GRANT LINKED
    if (isGrantLinkedActivity(parentActivityPlanTypeId)) {
      formData.grantId = activity.grantId || 0;
    }

    return formData;
  } else {
    let formData = {
      primaryObjective: '',
      secondaryObjective: '',
      name: '',
      activityNotes: '',
      firstName: '',
      surname: '',
      email: currentUserEmail,
      phone: '',
      entityName: '',
      country: '',
      address1: '',
      grantId: '',
      address2: '',
      suburb: '',
      state: '',
      postcode: '',
      projectedGrantAmount: null,
      description: '',
      organisationId: null,
      isExistingUser: false,
      relationship: PARTNER_RELATIONSHIP[0].value,
      emailConfirmError: '',
      managedBy: CREATE_LINKED_MANAGED_BY_OPTIONS()[0].value,
    };

    if (isGrantProgramActivity(activityPlanTypeId)) {
      formData.projectedTotalGrantPool = '';
    } else {
      // Projected Program Contribution
      formData.projectedGrantAmount = null;
    }

    if (isGrantLinkedActivity(parentActivityPlanTypeId)) {
      formData.grantId = '';
    }

    return formData;
  }
};

export const linkedActivitiesValidationSchema = (
  t,
  isSecondaryPolicy = false,
  activityPlanTypeId,
  parentActivityPlanTypeId,
  isPrimaryCultural = false,
  orgUsersEmails
) =>
  Yup.lazy((values) => {
    let validationSchema = {
      name: Yup.string().required(
        t('formulary.validation.activity_name_required')
      ),
      activityNotes: Yup.string().optional(),
      emailConfirmError: Yup.string().optional(),
    };

    if (
      get(values, 'managedBy', '') ===
      CREATE_LINKED_MANAGED_BY_OPTIONS()[2].value
    ) {
      validationSchema = {
        ...validationSchema,
        firstName: Yup.string().required(
          t('formulary.validation.first_name_required')
        ),
        surname: Yup.string().required(
          t('formulary.validation.last_name_required')
        ),
        email: Yup.string()
          .required(t('formulary.validation.activity_manager_required'))
          .matches(EMAIL_REG_EX, 'Email is not valid!'),
        phone: Yup.string()
          .required(t('formulary.validation.phone_number_required'))
          .matches(
            INSTANCE_PHONE_REG_EX,
            t('formulary.validation.please_enter_the_valid_format')
          )
          .min(
            INSTANCE_PHONE_MIN_LENGTH,
            t('formulary.validation.must_be_a_minimum_of_digits', {
              count: INSTANCE_PHONE_MIN_LENGTH,
            })
          ),
        managedBy: Yup.string()
          .oneOf(CREATE_LINKED_MANAGED_BY_OPTIONS().map((item) => item.value))
          .required(),
      };
    }

    if (values.managedBy === CREATE_LINKED_MANAGED_BY_OPTIONS()[2].value) {
      validationSchema = {
        ...validationSchema,
        email: Yup.string()
          .notOneOf(
            orgUsersEmails,
            t(
              'formulary.validation.unable_to_invite_organisation_user_as_external'
            )
          )
          .required(t('formulary.validation.activity_manager_required'))
          .matches(EMAIL_REG_EX, t('formulary.validation.email_not_valid')),
      };
    }

    const isProgramActivity = isParentProgramActivity(activityPlanTypeId);

    if (isProgramActivity) {
      validationSchema.primaryObjective = Yup.array().required(
        t('formulary.validation.primary_objective_required')
      );
      validationSchema.secondaryObjective =
        isSecondaryPolicy && !isPrimaryCultural
          ? Yup.array().required(
              t('formulary.validation.secondary_objective_required')
            )
          : Yup.array().optional();
    } else {
      validationSchema.primaryObjective = Yup.string().required(
        t('formulary.validation.primary_objective_required')
      );
      validationSchema.secondaryObjective =
        isSecondaryPolicy && !isPrimaryCultural
          ? Yup.string().required(
              t('formulary.validation.secondary_objective_required')
            )
          : Yup.string().optional();
    }

    if (values.relationship !== PARTNER_RELATIONSHIP_VALUES.NETWORK) {
      validationSchema.projectedGrantAmount = Yup.number().required(
        t('formulary.validation.program_contribution_required')
      );
    }

    //GRANT LINKED
    if (isGrantLinkedActivity(parentActivityPlanTypeId)) {
      validationSchema.grantId = Yup.string().optional();
    }

    return Yup.object().shape(validationSchema);
  });

export const linkedActivitiesGuestUserValidation = (
  t,
  isSecondaryPolicy = false,
  activityPlanTypeId,
  parentActivityPlanTypeId,
  isPrimaryCultural = false,
  orgUsersEmails = []
) =>
  Yup.lazy((values) => {
    let validationSchema = {
      name: Yup.string().required(
        t('formulary.validation.activity_name_required')
      ),
      email: Yup.string()
        .required(t('formulary.validation.activity_manager_required'))
        .matches(EMAIL_REG_EX, t('formulary.validation.email_not_valid')),
      relationship: Yup.string()
        .oneOf(['IN_KIND', 'FUNDING', 'NETWORK'])
        .required(),
      activityNotes: Yup.string().optional(),
      address2: Yup.string().optional().nullable(),
      description: Yup.string().optional(),
      emailConfirmError: !values.isConfirmedUser
        ? Yup.string().required(t('formulary.validation.confirmation_required'))
        : Yup.string().optional(),
      managedBy: Yup.string()
        .oneOf(CREATE_LINKED_MANAGED_BY_OPTIONS().map((item) => item.value))
        .required(),
    };

    if (values.managedBy === CREATE_LINKED_MANAGED_BY_OPTIONS()[2].value) {
      validationSchema = {
        ...validationSchema,
        email: Yup.string()
          .notOneOf(
            orgUsersEmails,
            t(
              'formulary.validation.unable_to_invite_organisation_user_as_external'
            )
          )
          .required(t('formulary.validation.activity_manager_required'))
          .matches(EMAIL_REG_EX, t('formulary.validation.email_not_valid')),
      };
    }

    if (values.isExistingUser) {
      validationSchema = {
        ...validationSchema,
        firstName: Yup.string().optional().nullable(),
        surname: Yup.string().optional().nullable(),

        phone: Yup.string()
          .optional()
          .matches(
            INSTANCE_PHONE_REG_EX,
            t('formulary.validation.please_enter_the_valid_format')
          )
          .min(
            INSTANCE_PHONE_MIN_LENGTH,
            t('formulary.validation.must_be_a_minimum_of_digits', {
              count: INSTANCE_PHONE_MIN_LENGTH,
            })
          )
          .nullable(),
        entityName: Yup.string().optional().nullable(),
        country: Yup.string().optional().nullable(),
        address1: Yup.string().optional().nullable(),
        suburb: Yup.string().optional().nullable(),
        state: Yup.string().optional().nullable(),
        postcode: Yup.string().optional().nullable(),
      };
    } else {
      validationSchema = {
        ...validationSchema,
        firstName: Yup.string().required(
          t('formulary.validation.first_name_required')
        ),
        surname: Yup.string().required(
          t('formulary.validation.last_name_required')
        ),
        phone: Yup.string()
          .required(t('formulary.validation.phone_number_required'))
          .matches(
            INSTANCE_PHONE_REG_EX,
            t('formulary.validation.please_enter_the_valid_format')
          )
          .min(
            INSTANCE_PHONE_MIN_LENGTH,
            t('formulary.validation.must_be_a_minimum_of_digits', {
              count: INSTANCE_PHONE_MIN_LENGTH,
            })
          ),

        entityName: Yup.string().required(
          t('formulary.validation.business_organisation_name_required')
        ),
        country: Yup.string().required(
          t('formulary.validation.country_required')
        ),
        address1: Yup.string().required(
          t('formulary.validation.address_line_1_required')
        ),
        suburb: Yup.string().required(
          t('formulary.validation.suburb_required')
        ),
        state: Yup.string().required(t('formulary.validation.state_required')),
        postcode: Yup.string().required(
          t('formulary.validation.postcode_required')
        ),
      };
    }

    const isProgramActivity = isParentProgramActivity(activityPlanTypeId);

    if (isProgramActivity) {
      validationSchema.primaryObjective = Yup.array().required(
        t('formulary.validation.primary_objective_required')
      );

      validationSchema.secondaryObjective =
        isSecondaryPolicy && !isPrimaryCultural
          ? Yup.array().required(
              t('formulary.validation.secondary_objective_required')
            )
          : Yup.array().optional();
    } else {
      validationSchema.primaryObjective = Yup.string().required(
        t('formulary.validation.primary_objective_required')
      );

      validationSchema.secondaryObjective =
        isSecondaryPolicy && !isPrimaryCultural
          ? Yup.string().required(
              t('formulary.validation.secondary_objective_required')
            )
          : Yup.string().optional();
    }

    // GRANT PROGRAM
    // if (isGrantProgramActivity(activityPlanTypeId)) {
    //   validationSchema.projectedTotalGrantPool = Yup.string().required(
    //     'Projected Total Grant Pool is required!'
    //   );
    // } else {
    if (values.relationship !== PARTNER_RELATIONSHIP_VALUES.NETWORK) {
      validationSchema.projectedGrantAmount = Yup.number().required(
        t('formulary.validation.program_contribution_required')
      );
    }
    // }

    //GRANT LINKED
    if (isGrantLinkedActivity(parentActivityPlanTypeId)) {
      validationSchema.grantId = Yup.string()
        .required(t('formulary.validation.grant_id_required'))
        .matches(grantIdPattern, t('formulary.validation.grant_id_not_valid'));

      // need to validate as required organisation address details in guest user
    } else {
      // single linked && Not single grant linked || program activities
    }

    return Yup.object().shape(validationSchema);
  });

export const linkedActivitiesCreateErrorObject = (
  errors,
  guestUser,
  activityPlanTypeId,
  parentActivityPlanTypeId,
  orgUsersEmails = []
) => {
  let formErrors = {};

  formErrors.primaryObjective = errors.primaryObjective
    ? 'primaryObjective'
    : null;
  formErrors.secondaryObjective = errors.secondaryObjective
    ? 'secondaryObjective'
    : null;
  formErrors.name = errors.name ? 'name' : null;
  formErrors.activityNotes = errors.activityNotes ? 'activityNotes' : null;
  if (guestUser) {
    formErrors.surname = errors.surname ? 'firstName' : null;
    formErrors.email = errors.email ? 'email' : null;
    formErrors.phone = errors.phone ? 'phone' : null;
    formErrors.entityName = errors.entityName ? 'entityName' : null;
    formErrors.country = errors.country ? 'country' : null;
    formErrors.address1 = errors.address1 ? 'address1' : null;
    formErrors.address2 = errors.address2 ? 'address2' : null;
    formErrors.suburb = errors.suburb ? 'suburb' : null;
    formErrors.state = errors.entityName ? 'state' : null;
    formErrors.postcode = errors.entityName ? 'postcode' : null;
    formErrors.relationship = errors.relationship ? 'relationship' : null;
    formErrors.managedBy = errors.managedBy ? 'managedBy' : null;
  }

  // GRANT PROGRAM
  if (isGrantProgramActivity(activityPlanTypeId)) {
    formErrors.projectedTotalGrantPool = errors.projectedTotalGrantPool
      ? 'projectedTotalGrantPool'
      : null;
  } else {
    // Projected Program Contribution
    formErrors.projectedGrantAmount = errors.projectedGrantAmount
      ? 'projectedGrantAmount'
      : null;
  }

  //GRANT LINKED
  if (isGrantLinkedActivity(parentActivityPlanTypeId)) {
    formErrors.grantId = errors.grantId ? 'grantId' : null;
  }

  return formErrors;
};
